/**
 * Pending datapoints panel (contracts-plan.md §7.3, R15, G11): every
 * proposed datapoint of the network, grouped by the version it came from,
 * with accept, reject and accept-all per version. Nothing here has entered
 * the rate card yet — "the tool recommends; a person decides."
 */
import { useCallback, useEffect, useState } from 'react';
import { CheckCheck, Check, X as XIcon, Clock } from 'lucide-react';
import { EmptyState, outlineBtnCls, iconBtnCls, useToast } from '../../../ui';
import { getPendingDatapoints, acceptDatapoint, rejectDatapoint, acceptAllDatapoints } from '../../api/rateCard';
import type { RcDatapoint } from '../../api/rateCard';

interface Props {
  /** Called after an accept, reject or accept-all that could change the card (accept/accept-all only). */
  onChanged: () => void;
}

interface VersionGroup {
  versionId: string;
  rows: RcDatapoint[];
}

function groupByVersion(rows: RcDatapoint[]): VersionGroup[] {
  const map = new Map<string, RcDatapoint[]>();
  for (const row of rows) {
    if (!map.has(row.version_id)) map.set(row.version_id, []);
    map.get(row.version_id)!.push(row);
  }
  return [...map.entries()].map(([versionId, rowsForVersion]) => ({ versionId, rows: rowsForVersion }));
}

export function PendingPanel({ onChanged }: Props) {
  const { toast } = useToast();
  const [rows, setRows] = useState<RcDatapoint[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const refresh = useCallback(() => {
    getPendingDatapoints()
      .then((result) => setRows(result.datapoints))
      .catch(() => setRows([]));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function handleAccept(id: string) {
    setBusyId(id);
    try {
      await acceptDatapoint(id);
      toast('Datapoint accepted.', { type: 'success' });
      refresh();
      onChanged();
    } catch {
      toast('Could not accept that datapoint.', { type: 'error' });
    } finally {
      setBusyId(null);
    }
  }

  async function handleReject(id: string) {
    setBusyId(id);
    try {
      await rejectDatapoint(id);
      toast('Datapoint rejected.', { type: 'info' });
      refresh();
    } catch {
      toast('Could not reject that datapoint.', { type: 'error' });
    } finally {
      setBusyId(null);
    }
  }

  async function handleAcceptAll(versionId: string) {
    setBusyId(versionId);
    try {
      const result = await acceptAllDatapoints(versionId);
      toast(`Accepted ${result.accepted.length} datapoint(s).`, { type: 'success' });
      refresh();
      onChanged();
    } catch {
      toast('Could not accept all for that version.', { type: 'error' });
    } finally {
      setBusyId(null);
    }
  }

  if (rows === null) {
    return <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>;
  }

  if (rows.length === 0) {
    return (
      <EmptyState
        icon={Clock}
        title="Nothing pending"
        hint="Proposed datapoints from executed versions will appear here for review (§7.3)."
        variant="card"
      />
    );
  }

  const groups = groupByVersion(rows);

  return (
    <div className="space-y-3" data-testid="rc-pending-panel">
      {groups.map((group) => (
        <div key={group.versionId} className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-4 py-2 dark:border-gray-700 dark:bg-gray-800/50">
            <span className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Version {group.versionId.slice(0, 8)} — {group.rows.length} pending
            </span>
            <button
              type="button"
              className={outlineBtnCls()}
              disabled={busyId === group.versionId}
              onClick={() => handleAcceptAll(group.versionId)}
              data-testid={`rc-accept-all-${group.versionId}`}
            >
              <CheckCheck className="h-4 w-4" />
              Accept all
            </button>
          </div>
          <ul className="divide-y divide-gray-200 dark:divide-gray-700">
            {group.rows.map((dp) => (
              <li
                key={dp.id}
                data-testid={`rc-pending-${dp.id}`}
                className="flex items-center justify-between gap-3 px-4 py-2 text-sm"
              >
                <div className="min-w-0 truncate">
                  <span className="font-medium text-gray-900 dark:text-gray-100">{dp.entry_name ?? dp.entry_id}</span>
                  <span className="ml-2 text-gray-500 dark:text-gray-400">{dp.kind}</span>
                  <span className="ml-2 text-gray-500 dark:text-gray-400">
                    {dp.value ?? dp.value_text} · {dp.sponsor_name ?? 'unknown sponsor'} · {dp.agreed_date}
                  </span>
                </div>
                <div className="flex flex-shrink-0 items-center gap-1.5">
                  <button
                    type="button"
                    aria-label="Accept"
                    title="Accept"
                    className={iconBtnCls}
                    disabled={busyId === dp.id}
                    onClick={() => handleAccept(dp.id)}
                    data-testid={`rc-accept-${dp.id}`}
                  >
                    <Check className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    aria-label="Reject"
                    title="Reject"
                    className={iconBtnCls}
                    disabled={busyId === dp.id}
                    onClick={() => handleReject(dp.id)}
                    data-testid={`rc-reject-${dp.id}`}
                  >
                    <XIcon className="h-4 w-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

export default PendingPanel;
